import React, {FC, useEffect, useRef, useState} from "react";
import {t} from "i18next";
import {
    IStockConvertRule,
    IStocksExportRuleForWarehouse,
    StockConvertRuleType
} from "../../../../constants/StocksExport/IStocksExportRuleForWarehouse";
import Toggle from "../../../Toggle/Toggle";
import Icon from "../../../commons/Icons";
import {warehouseTypeLabels} from "../../../Tables/WarehouseTable/parse-warehouse-type";
import {Input} from "../../../commons";
import classNames from "classnames";
import path_up from 'assets/images/closeSelectIcon.svg';
import path_down from 'assets/images/openSelectIcon.svg';
import DateTimeInput from "components/commons/Input/DateTimeInput";
import {IStocksReserveRuleForWarehouse} from "../../../../constants/StocksReserve/IStocksReserveRuleForWarehouse";
import ExportSourceAndReservingWhsSelector from "../../../Stocks/ExportSourceAndReservingWhsSelector";
import {IStocksWriteOffWarehouse} from "../../../../constants/StocksReserve/IStocksWriteOffWarehouse";
import FormSelect, {ISelectOption} from "components/commons/Select/FormSelect";
import FilteringOnlyForHighSla from "../FilteringOnlyForHighSla";
import {IProductsFilter} from "../../../../api/services/sellerProductsService";
import {SlidingContainer} from "../../../SlidingContainer/SlidingContainer";
import settingsIcon from "../../../../assets/images/gear-dark.svg";
import {Button} from "../../../buttons";
import plus from "../../../../assets/icons/commons/roundPlus.svg";
import {ConvertRuleRow} from "./ConvertRuleRow";

interface RowProps {
    index: number
    freeShare: number,
    sellerID: number,
    isEnabledAutoExportForSeller: boolean,
    isEnabledAutoReservingForSeller: boolean,
    isDisabledAutoReserving: boolean,
    isFilteringAvailableForSLA: boolean,
    data: IStocksExportRuleForWarehouse,
    reserveData: IStocksReserveRuleForWarehouse,
    updateDataHandler: (data: IStocksExportRuleForWarehouse) => void,
    updateReserveDataHandler: (data: IStocksReserveRuleForWarehouse) => void,
    isDisclosed: boolean,
    toggleIsDisclosed: (index: number) => void,
    productFilters: IProductsFilter[]
}

const initialStockConvertRule: IStockConvertRule = {
    contains: 0,
    operator: '=',
    count: 0,
}

const Row: FC<RowProps> = (
    {
       index,
       freeShare,
       sellerID,
       isEnabledAutoExportForSeller,
       isEnabledAutoReservingForSeller,
       isDisabledAutoReserving,
       isFilteringAvailableForSLA,
       data,
       reserveData,
       updateDataHandler,
       updateReserveDataHandler,
       isDisclosed,
       toggleIsDisclosed,
       productFilters
    }) => {

    const [sourceWhID, setSourceWhID] = useState<string>('')
    const [percentageOfTotal, setPercentageOfTotal] = useState<string>('0')
    const [isEnableAutoExport, setIsEnableAutoExport] = useState<boolean>(false)
    const [isEnabledAutoReserving, setIsEnabledAutoReserving] = useState<boolean>(false)
    const [reservingFromDate, setReservingFromDate] = useState<string>('')
    const [isDistribute, setIsDistribute] = useState<boolean>(false)
    const [isOpenStockConvertRuleForm, setIsOpenStockConvertRuleForm] = useState<boolean>(false)
    const [stockConvertRuleType, setStockConvertRuleType] = useState<StockConvertRuleType>(StockConvertRuleType.Percentage)
    const [stockConvertRules, setStockConvertRules] = useState<IStockConvertRule[]>([]);

    useEffect(() => {
        setPercentageOfTotal(data.percentageOfTotal.toString())
        setIsEnableAutoExport(data.isEnableAutoExport)
        setIsDistribute(data.isDistribute)
        setSourceWhID(data.sourceWhID)
        setIsEnabledAutoReserving(reserveData.isReservingByDeltaAndOrder)
        if (data.stockConvertRuleType.length > 0) {
            stockConvertRuleTypeChangeHandler(data.stockConvertRuleType)
        } else {
            stockConvertRuleTypeChangeHandler(data.isDistribute ? StockConvertRuleType.Distribute : StockConvertRuleType.Percentage)
        }
        setStockConvertRules(data.stockConvertRules?.length > 0 ? data.stockConvertRules : [initialStockConvertRule])
    }, [])

    useEffect(() => {
        isDistributeToggleHandler(stockConvertRuleType === StockConvertRuleType.Distribute)
    }, [stockConvertRuleType])

    useEffect(() => {
        // Без этого if невозможно редактировать поле, постоянное срабрасывается до исходного
        if (isDistribute) {
            setPercentageOfTotal(data.percentageOfTotal.toString())
        }
        setPercentageOfTotal(data.percentageOfTotal.toString())

        // Открыть подформу если не активированы обязательные зависимые поля в подформе
        if ((reserveData.stocksWriteOffWarehouses || []).length === 0) {
            if (reserveData.isReservingByDeltaAndOrder && !isDisclosed) {
                toggleIsDisclosed(index)
            }
        }
    }, [data])

    const sourceWHChangeHandler = (whUUID: string) => {
        setSourceWhID(whUUID)
        data.sourceWhID = whUUID
        updateDataHandler(data)
    }

    const isEnableAutoExportToggleHandler = (isEnableAutoExport: boolean) => {
        setIsEnableAutoExport(isEnableAutoExport)
        data.isEnableAutoExport = isEnableAutoExport
        updateDataHandler(data)

        // Открыть подформу если не активированы обязательные зависимые поля в подформе
        if ((data.sourceWhIDs || []).length === 0) {
            if (data.isEnableAutoExport && !isDisclosed) {
                toggleIsDisclosed(index)
            }
        }
    }

    const isEnabledAutoReservingToggleHandler = (isEnabledAutoReserving: boolean) => {
        setIsEnabledAutoReserving(isEnabledAutoReserving)
        reserveData.isReservingByDeltaAndOrder = isEnabledAutoReserving
        updateReserveDataHandler(reserveData)

        // Открыть подформу если не активированы обязательные зависимые поля в подформе
        if ((reserveData.stocksWriteOffWarehouses || []).length === 0) {
            if (reserveData.isReservingByDeltaAndOrder && !isDisclosed) {
                toggleIsDisclosed(index)
            }
        }
    }

    const reservingFromDateChangeHandler = (dateContainer: Date[]) => {
        const reservingFromDate = dateContainer[0].toISOString().substring(0, 19) + 'Z'
        setReservingFromDate(reservingFromDate)
        reserveData.reservingFromDate = reservingFromDate
        updateReserveDataHandler(reserveData)
    }

    const isDistributeToggleHandler = (isDistribute: boolean) => {
        setIsDistribute(isDistribute)
        data.isDistribute = isDistribute
        updateDataHandler(data)
    }

    const onChangeProductFilter = (filterID: string) => {
        data.filterID = filterID === '' ? null : parseInt(filterID)
        updateDataHandler(data)
    }

    const onChangeIsTakeAllWhsAsSource = (isTakeAllWhsAsSource: boolean) => {
        data.isTakeAllWhsAsSource = isTakeAllWhsAsSource
        updateDataHandler(data)
    }

    const onChangeSourceWhIDs = (whs: string[]) => {
        data.sourceWhIDs = whs
        updateDataHandler(data)
    }

    const onChangeIsWriteOffAllWhs = (isWriteOffAllWhs: boolean) => {
        reserveData.isWriteOffAllWhs = isWriteOffAllWhs
        updateReserveDataHandler(reserveData)
    }

    const onChangeWriteOffWhs = (whs: IStocksWriteOffWarehouse[]) => {
        reserveData.stocksWriteOffWarehouses = whs
        updateReserveDataHandler(reserveData)
    }

    const percentageOfTotalChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        let v = parseInt(e.target.value)
        setPercentageOfTotal(v.toString())
        data.percentageOfTotal = v
        updateDataHandler(data)
    }

    const stockConvertRuleTypeChangeHandler = (t: StockConvertRuleType) => {
        setStockConvertRuleType(t)
        data.stockConvertRuleType = t
        updateDataHandler(data)
    }

    const isExternalWh = data.warehouse.externalService.length > 0
    const typeLabel = t(warehouseTypeLabels[data.warehouse.type] || '')

    const contentRef = useRef<HTMLDivElement>(null);

    // stock convert rules
    const deleteStockConvertRule = (position: number) => {
        let rr: IStockConvertRule[] = []
        if (position === 0 && stockConvertRules.length === 1) {
            rr = [initialStockConvertRule]
        } else {
            rr = stockConvertRules.filter((_, i) => i !== position)
        }
        setStockConvertRules(rr)
        data.stockConvertRules = rr
        updateDataHandler(data)
    }

    const updateStockConvertRules = (position: number, payload: IStockConvertRule) => {
        let rr: IStockConvertRule[] = []
        if (position === 0 && stockConvertRules.length === 1) {
            rr = [payload]
        } else {
            rr = stockConvertRules.map((v, i) => i === position ? {...payload} : v)
        }
        setStockConvertRules(rr)
        data.stockConvertRules = rr
        updateDataHandler(data)
    }

    const getFilterValue = () => {
        if (data.filterID === 0 || !data.filterID?.toString()) return '';

        const filterExists = productFilters.some(f => f.id.toString() === data.filterID?.toString());
        return filterExists ? data.filterID.toString() : 'del';
    };

    const hasInvalidFilter = (): boolean => {
        return data.filterID !== 0 && !!data.filterID?.toString() &&
            !productFilters.some(f => f.id.toString() === data.filterID?.toString());
    };

    const getFilterErrorDescription = (): string => {
        return hasInvalidFilter() ? 'Выбранный фильтр удален, установите актуальный' : '';
    };

    const getFilterOptions = (): {text: string, value: string}[] => {
        let options = [{text: 'Все', value: ''}, ...productFilters.map(f => ({value: f.id.toString(), text: f.name} as ISelectOption))];
        if (hasInvalidFilter()) {
            options.push({text: '-', value: 'del'})
        }
        return options;
    };

    // @ts-ignore
    return (
        <>
            <tr className={classNames(!isDisclosed && "border-b border-gray-200 py-2") }>
                <td onClick={() => toggleIsDisclosed(index)} className={"cursor-pointer"}>
                    <>
                        {data.warehouse.name + (isExternalWh && data.warehouse.externalID ? `, ${data.warehouse.externalID}` : '')}
                        <div className={`flex flex-row`}>
                            { isExternalWh &&
                                <span className={`flex align-middle mr-2`}>
                                    <Icon width={16} height={16} marketSource={data.warehouse.externalService} />
                                </span>
                            }
                            <span className={`text-[12px] text-gray-400`}>{typeLabel}</span>
                        </div>
                    </>
                </td>

                <td className="py-3 pr-[20px]">
                    <Toggle
                        isChecked={isEnabledAutoReserving}
                        toggleHandler={isEnabledAutoReservingToggleHandler}
                        disabled={isDisabledAutoReserving}
                    ></Toggle>
                </td>


                <td className="py-3 pr-[20px] whitespace-nowrap">
                    <DateTimeInput changeHandler={reservingFromDateChangeHandler} name={"d"} value={reserveData?.reservingFromDate || ''}></DateTimeInput>
                </td>

                <td>
                    <Toggle
                        isChecked={isEnableAutoExport}
                        toggleHandler={isEnableAutoExportToggleHandler}
                    ></Toggle>
                </td>

                <td className="py-3 pr-[20px] whitespace-nowrap ">
                    <div className="flex items-center justify-end">
                        <div
                            className="cursor-pointer flex items-center"
                            onClick={() => setIsOpenStockConvertRuleForm(true)}
                        >
                            {stockConvertRuleType === StockConvertRuleType.Percentage && <span>{percentageOfTotal}%</span>}
                            {stockConvertRuleType === StockConvertRuleType.Distribute && <span className="text-gray-400">{percentageOfTotal}%</span>}
                            {stockConvertRuleType === StockConvertRuleType.Rules && <span>f(x)</span>}
                            <span className={`ml-2`}><img src={settingsIcon} alt="" className="w-4 h-4 text-black" /></span>
                        </div>
                    </div>

                    <SlidingContainer
                        //@ts-ignore
                        title="Настройка отправки канала"
                        id="stocks-export-row-form-modal"
                        modalOpen={isOpenStockConvertRuleForm}
                        toCloseModal={() => setIsOpenStockConvertRuleForm(false)}
                        toCloseModalOnClickOutside={() => setIsOpenStockConvertRuleForm(false)}
                        modalContent={contentRef}
                        minWidth={720}
                    >
                        <div className="border-b-[1px] pb-2 mb-4">
                            <FormSelect
                                id={data.warehouse.uuid + '_stockConvertRuleType'}
                                name={data.warehouse.uuid + '_stockConvertRuleType'}
                                value={stockConvertRuleType}
                                label={'Выберите тип правила'}
                                changeHandler={(e) => stockConvertRuleTypeChangeHandler(e.target.value)}
                                customClassName={'w-full mb-3'}
                                customLabelClassName="text-sm"
                                blurHandler={event => {}}
                                validationError={false}
                                validationErrorDescription={''}
                                options={[
                                    {text: 'Доли, %', value: StockConvertRuleType.Percentage},
                                    {text: 'Равномерное распределение остатка между каналами', value: StockConvertRuleType.Distribute},
                                    {text: 'Условные выражения, f(x)', value: StockConvertRuleType.Rules},
                                ]}
                                require={false}
                            ></FormSelect>
                        </div>

                        {stockConvertRuleType === StockConvertRuleType.Percentage &&
                            <>
                                <Input
                                    placeholder={''}
                                    id={data.warehouse.uuid + '_share_value'}
                                    name={data.warehouse.uuid + '_share_value'}
                                    value={percentageOfTotal}
                                    min={0}
                                    label={'Доля от суммы остатоков активных источников канала, %'}
                                    type="number"
                                    changeHandler={percentageOfTotalChangeHandler}
                                    customClassName={classNames("text-[14px] pr-0 w-1/4 text-gray-600", isDistribute ? 'text-gray-400 hover:border-slate-200' : '')}
                                    customLabelClassName={"text-sm"}
                                    validationError={false}
                                    validationErrorDescription={''}
                                    disabled={isDistribute}
                                />
                            </>
                        }


                        {stockConvertRuleType === StockConvertRuleType.Distribute &&
                            <>
                                <Input
                                    placeholder={''}
                                    id={data.warehouse.uuid + '_distributed_value'}
                                    name={data.warehouse.uuid + '_distributed_value'}
                                    value={percentageOfTotal}
                                    min={0}
                                    label={'Доля от суммы остатоков всех источников, %'}
                                    type="number"
                                    changeHandler={() => {}}
                                    customClassName={classNames("text-[14px] pr-0 w-1/4 text-gray-600", isDistribute ? 'text-gray-400 hover:border-slate-200' : '')}
                                    customLabelClassName={"text-sm"}
                                    validationError={false}
                                    validationErrorDescription={''}
                                    readOnly={true}
                                    require={false}
                                />
                            </>
                        }

                        {stockConvertRuleType === StockConvertRuleType.Rules &&
                            <>
                                <div className="mb-4">
                                    Приоритет правил - сверху-вниз
                                </div>
                                {stockConvertRules.map((r, k) =>
                                    <ConvertRuleRow key={k} position={k} rulesCount={stockConvertRules.length} data={r} deleteRule={deleteStockConvertRule} updateDataHandler={updateStockConvertRules}></ConvertRuleRow>
                                )}
                                <div className={`text-right mt-4`}>
                                    <Button
                                        clickHandler={() => setStockConvertRules([...stockConvertRules, initialStockConvertRule])}
                                        appearance="primary"
                                        customClassName="button-add"
                                        type="button"
                                    >
                                        <div className="flex justify-around w-[150px] items-center">
                                            <img src={plus} alt="" className="w-4 h-4" />
                                            <div>Добавить правило</div>
                                        </div>
                                    </Button>
                                </div>
                            </>
                        }

                        <div className="flex justify-center absolute bottom-0 border-t-[1px] px-3 w-full sm:min-w-[511px] right-0 py-3 bg-white">
                            <Button
                                type="button"
                                clickHandler={() => setIsOpenStockConvertRuleForm(false)}
                                appearance="primary"
                                customClassName="w-full sm:w-5/6 py-2.5"
                            >Применить</Button>
                        </div>

                    </SlidingContainer>
                </td>

                <td onClick={() => toggleIsDisclosed(index)} className={"cursor-pointer"}>
                    <img src={isDisclosed ? path_up : path_down} alt={''}/>
                </td>
            </tr>
            {
                isDisclosed &&
                <tr className="border-b border-gray-200 py-2">
                    <td colSpan={6}>

                        <div className={`my-3 mt-7`}>
                            <FormSelect
                                id={data.warehouse.uuid + '_filter'}
                                name={data.warehouse.uuid + '_filter'}
                                value={getFilterValue()}
                                label={'Ограничить отправку на склад по фильтру'}
                                changeHandler={(e) => onChangeProductFilter(e.target.value)}
                                customClassName={'w-5/12'}
                                customLabelClassName="text-sm"
                                blurHandler={event => {}}
                                validationError={hasInvalidFilter()}
                                validationErrorDescription={getFilterErrorDescription()}
                                options={getFilterOptions()}
                                require={false}
                                disabled={!isFilteringAvailableForSLA}
                            ></FormSelect>
                            {!isFilteringAvailableForSLA && <div className={`mt-2`}><FilteringOnlyForHighSla></FilteringOnlyForHighSla></div>}
                        </div>
                        <div className={`my-3 mt-7`}>
                            <ExportSourceAndReservingWhsSelector
                                sellerID={sellerID}
                                isEnabledAutoReserving={isEnabledAutoReserving}
                                isEnableAutoExport={isEnableAutoExport}
                                isDistribute={data.isDistribute}
                                isTakeAllWhsAsSource={data.isTakeAllWhsAsSource}
                                onChangeIsTakeAllWhsAsSource={onChangeIsTakeAllWhsAsSource}
                                sourceWhIDs={data.sourceWhIDs || []}
                                onChangeSourceWhIDs={onChangeSourceWhIDs}
                                isWriteOffAllWhs={reserveData.isWriteOffAllWhs}
                                onChangeIsWriteOffAllWhs={onChangeIsWriteOffAllWhs}
                                stocksWriteOffWarehouses={reserveData.stocksWriteOffWarehouses || []}
                                onChangeWriteOffWhs={onChangeWriteOffWhs}
                            ></ExportSourceAndReservingWhsSelector>
                        </div>
                    </td>
                </tr>
            }
        </>
    )
}

export default Row
